import { AddressInterface } from '@hermes/types'
import { getPublicConfigVariable } from '@lib/utils/getConfigVariable'
import { z } from 'zod'

const apiPath = getPublicConfigVariable('LEGACY_API_URL')
const addressApiPath = getPublicConfigVariable('ADDRESS_AUTOCOMPLETE_API_URL')
const addressApiKey = getPublicConfigVariable('ADDRESS_AUTOCOMPLETE_API_KEY')

const CreateAddressBodySchema = z.object({
  primary: z.boolean(),
  deliveryPlace: z.null(),
  isActive: z.boolean(),
  country: z.string(),
  city: z.string().optional(),
  streetAddress: z.string().optional(),
  hasSupport: z.boolean().default(false),
  deliveryPointId: z.number().optional(),
  entrance: z.string().optional().nullable(),
  coordinates: z.tuple([z.number(), z.number()]).optional(),
  houseType: z.string().optional(),
  zip: z.string(),
  streetNumeric: z.number().optional(),
  floorNo: z.number().optional(),
  flatNo: z.number().optional(),
  flatAlias: z.string().default(''),
  floorType: z.string().optional(),
  floorTypeName: z.string().optional().nullable(),
  householdDeliveryPointId: z.number().optional()
})

const CheckSupportHelthjemBodySchema = z.object({
  streetAddress: z.string(),
  streetNumeric: z.number(),
  zip: z.string(),
  city: z.string(),
  entrance: z.string().optional().nullable(),
  floorType: z.string().optional().nullable(),
  floorNo: z.number().optional().nullable(),
  flatNo: z.number().optional().nullable()
})

export const createUserAddress = async ({
  country,
  token,
  formData
}: {
  token: any
  country: string
  formData: any
}) => {
  const address = {
    ...formData.street?.value,
    ...formData.streetNumber?.value,
    ...formData.floor?.value,
    ...formData.household?.value,
    isPrimary: formData.isPrimary,
    householdDeliveryPointId: formData.household?.value?.deliveryPointId,
    deliveryPointId: formData.household?.value?.deliveryPointId
  }
  const { hasSupport: hasPickupSupport } = await checkPickupSupport(address, country)

  const formattedPostalCode = address?.postalCode.replace(' ', '')
  const parsedBody = CreateAddressBodySchema.safeParse({
    primary: address.isPrimary,
    deliveryPlace: null,
    isActive: true,
    country,
    city: address?.city,
    streetAddress: address?.streetName,
    hasSupport: hasPickupSupport || false,
    deliveryPointId: address?.deliveryPointId,
    entrance: address?.entrance,
    houseType: address.houseType,
    zip: formattedPostalCode,
    streetNumeric: address.streetNo,
    floorNo: address.floorNo,
    flatNo: address.flatNo,
    flatAlias: '',
    floorType: address.floorType,
    floorTypeName: address.floorTypeName,
    householdDeliveryPointId: address.householdDeliveryPointId,
    ...(address.latitude && { coordinates: [address.latitude, address.longitude] })
  })

  if (!parsedBody.success) {
    console.error('Invalid address format', parsedBody.error)
    throw new Error('Invalid address format')
  }

  const validBody = parsedBody.data

  const response = await fetch(`${apiPath}/address`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      country
    },
    body: JSON.stringify(validBody)
  })

  if (!response.ok) {
    throw new Error('Failed to save address')
  }

  const savedAddress = await response.json()
  return savedAddress.data
}

export const checkPickupSupport = async (address: any, countryCode: string) => {
  if (!countryCode) throw new Error('Country not specified')
  const country = countryCode.toUpperCase()

  const parsedAddress = CheckSupportHelthjemBodySchema.safeParse({
    streetAddress: address.streetName,
    streetNumeric: address.streetNo,
    zip: address.postalCode,
    city: address.city,
    entrance: address.entrance,
    floorType: address.floorType,
    floorNo: address.floorNo,
    flatNo: address.flatNo
  })

  if (!parsedAddress.success) {
    console.error('Invalid address format', parsedAddress.error)
    throw new Error('Invalid address format')
  }

  const validAddress = parsedAddress.data

  try {
    const { city, streetAddress, streetNumeric, zip, floorNo, flatNo } = validAddress
    const formattedZip = zip.replace(' ', '')
    const isApartment = floorNo && flatNo
    const queryParams = new URLSearchParams({
      city,
      streetName: streetAddress,
      streetNumeric: streetNumeric.toString(),
      zip: formattedZip
    }).toString()

    const response = await fetch(`${addressApiPath}/checkSupportHelthjem?${queryParams}`, {
      headers: {
        'x-api-key': addressApiKey || '',
        country
      }
    })

    if (!response.ok) {
      throw new Error('Failed to check pickup support')
    }

    const responseJson = await response.json()

    return { hasSupport: responseJson.data?.hasSupport ?? false }
  } catch (err: any) {
    console.error('DI response error: ', err.message)
    // Return hasSupport as false in case of any error
    return {
      hasSupport: false
    }
  }
}

export const checkDeliverySupport = async (address: AddressInterface, token: string) => {
  const response = await fetch(`${apiPath}/address/${address.id}/pickup-support`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  const data = await response.json()
  return data.data
}
